import React, { useState, useEffect } from 'react';
import util from '../../utils/util';
import {
    Card, Button, Table, Modal, Input, Select, DatePicker,
    message, Popconfirm, Space, Tag, Row, Col, InputNumber,
    Progress, Statistic, Alert, Tooltip
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, TrophyOutlined,
    BarChartOutlined, SettingOutlined, InfoCircleOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { usePeriods, periodToForm } from '../../hooks/usePeriods';
import moment from 'moment';
import { trailCreate, trailUpdate, trailDelete, trailPageView, TRAIL_MODULES } from '../../utils/activityTrail';
import { SITE_TYPES as FACILITY_LEVELS } from '../../constants/facilityTypes';
const { RangePicker } = DatePicker;


// ─── Weight Slider ─────────────────────────────────────────────────────────────
function WeightSlider({ labelA, labelB, valueA, onChange, colorA = '#1890ff', colorB = '#52c41a' }) {
    const valueB = 100 - valueA;
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: colorA, fontSize: 13 }}>{labelA}: {valueA}%</span>
                <span style={{ fontWeight: 700, color: colorB, fontSize: 13 }}>{labelB}: {valueB}%</span>
            </div>
            <div style={{ position: 'relative', height: 32, display: 'flex', alignItems: 'center' }}>
                <div style={{ position: 'absolute', width: '100%', height: 12, borderRadius: 6, overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: `${valueA}%`, backgroundColor: colorA, transition: 'width 0.2s' }} />
                    <div style={{ flex: 1, backgroundColor: colorB }} />
                </div>
                <input type="range" min={0} max={100} value={valueA}
                    onChange={e => onChange(Number(e.target.value))}
                    style={{ position: 'relative', width: '100%', margin: 0, cursor: 'pointer',
                        WebkitAppearance: 'none', background: 'transparent', zIndex: 1 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: '#999' }}>
                <span>0%</span><span>50%</span><span>100%</span>
            </div>
        </div>
    );
}

// ─── Period Config Panel ──────────────────────────────────────────────────────
function PeriodWeightConfig({ clauses, onSaved }) {
    const { periods, currentPeriod } = usePeriods();

    const [form, setForm] = useState({
        period_label: '',
        period_start: null,
        period_end: null,
        qoc_weight: 80,
        survey_weight: 20,
        inpatient_weight: 50,
        outpatient_weight: 50,
        min_surveys_required: 0,
        min_surveys_pct: 10,
        notes: '',
    });
    const [loading, setLoading] = useState(false);
    // Permission checks
    const canAdd    = util.canAdd('qoc_kpis');
    const canEdit   = util.canEdit('qoc_kpis');
    const canDelete = util.canDelete('qoc_kpis');
    const canExport = util.canExport('qoc_kpis');
    const [existing, setExisting] = useState(false);

    const loadConfig = async (label) => {
        if (!label || label.length < 3) return;
        try {
            const res = await QoCService.getPeriodConfig(label);
            const d = res.data?.data;
            if (d && (d.qoc_weight || d.survey_weight)) {
                setForm(f => ({
                    ...f,
                    qoc_weight:           Number(d.qoc_weight ?? 80),
                    survey_weight:        Number(d.survey_weight ?? 20),
                    inpatient_weight:     Number(d.inpatient_weight ?? 50),
                    outpatient_weight:    Number(d.outpatient_weight ?? 50),
                    min_surveys_required: Number(d.min_surveys_required ?? 0),
                    min_surveys_pct:      Number(d.min_surveys_pct ?? 10),
                    notes:                d.notes ?? '',
                }));
                setExisting(true);
            } else {
                setExisting(false);
            }
        } catch { setExisting(false); }
    };

    const setQocWeight = (v) => setForm(f => ({ ...f, qoc_weight: v, survey_weight: 100 - v }));
    const setIpWeight  = (v) => setForm(f => ({ ...f, inpatient_weight: v, outpatient_weight: 100 - v }));

    const handleSave = async () => {
        if (!form.period_label || !form.period_start || !form.period_end) {
            message.error('Period label and dates are required');
            return;
        }
        setLoading(true);
        try {
            await QoCService.saveKpi({
                ...form,
                kpi_type:     'period_config',
                target_pct:   0,
                period_start: moment(form.period_start).format('YYYY-MM-DD'),
                period_end:   moment(form.period_end).format('YYYY-MM-DD'),
            });

            const action = existing ? trailUpdate : trailCreate;
            action(TRAIL_MODULES.KPI, 'Period Weights', form.period_label, {
                period: form.period_label,
                qocWeight: form.qoc_weight,
                surveyWeight: form.survey_weight,
            });
            message.success('Period weights saved successfully');
            setExisting(true);
            onSaved && onSaved();
        } catch (e) {
            message.error(e.response?.data?.message || 'Failed to save weights');
        } finally { setLoading(false); }
    };

    const totalCheck = (form.qoc_weight + form.survey_weight) === 100;
    const previewSurvey = (78 * form.inpatient_weight / 100) + (72 * form.outpatient_weight / 100);
    const previewIndex  = (85 * form.qoc_weight / 100) + (previewSurvey * form.survey_weight / 100);

    return (
        <div>
            <Alert type="info" showIcon
                message="Configure Quality Index Weights"
                description="Set what percentage of the Quality Index comes from the QoC assessment vs customer surveys, and how inpatient and outpatient surveys are weighted within that survey component. These settings apply to all facilities for the chosen period."
                style={{ marginBottom: 20 }} />

            <Card size="small" title="Assessment Period" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col span={8}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Period Label *</label>
                        <Select style={{ width: '100%' }}
                            value={form.period_label || undefined}
                            onChange={v => {
                                const p = periods.find(x => x.label === v);
                                if (p) {
                                    setForm(f => ({ ...f, ...periodToForm(p) }));
                                    loadConfig(v);
                                }
                            }}>
                            {periods.map(p => (
                                <Select.Option key={p.label} value={p.label}>
                                    {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                </Select.Option>
                            ))}
                        </Select>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Links KPI targets, weights and quality index calculations</div>
                    </Col>
                    <Col span={10}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Period Dates (auto-filled from period)</label>
                        <RangePicker
                            value={[form.period_start, form.period_end]}
                            disabled
                            style={{ width: '100%' }} />
                    </Col>
                    <Col span={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                        {existing && <Tag color="success" icon={<CheckCircleOutlined />}>Config exists — editing</Tag>}
                    </Col>
                </Row>
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}
                title={<span><BarChartOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                    Quality Index Composition
                    <Tooltip title="Quality Index = (QoC Assessment × QoC weight) + (Customer Survey × Survey weight). Must total 100%.">
                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#aaa' }} />
                    </Tooltip></span>}>
                <WeightSlider
                    labelA="QoC Assessment" labelB="Customer Survey"
                    valueA={form.qoc_weight} onChange={setQocWeight}
                    colorA="#1890ff" colorB="#52c41a" />
                <div style={{ marginTop: 12, padding: '10px 14px',
                    backgroundColor: totalCheck ? '#f6ffed' : '#fff2f0',
                    borderRadius: 6, border: `1px solid ${totalCheck ? '#b7eb8f' : '#ffccc7'}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: totalCheck ? '#52c41a' : '#f5222d' }}>
                        Total: {form.qoc_weight + form.survey_weight}%
                    </span>
                    <span style={{ fontSize: 12, color: '#888' }}>
                        Formula: (QoC × {form.qoc_weight}%) + (Survey × {form.survey_weight}%)
                    </span>
                    {!totalCheck && <Tag color="error">Must equal 100%</Tag>}
                </div>
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}
                title={<span><SettingOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                    Survey Component Split (within the {form.survey_weight}%)
                    <Tooltip title="Within the survey component, how much comes from Inpatient vs Outpatient surveys.">
                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#aaa' }} />
                    </Tooltip></span>}>
                <WeightSlider
                    labelA="Inpatient Surveys" labelB="Outpatient Surveys"
                    valueA={form.inpatient_weight} onChange={setIpWeight}
                    colorA="#0050b3" colorB="#006837" />
                <div style={{ marginTop: 10, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                    If only one survey type has been captured, 100% of the survey component uses that type automatically.
                </div>
                <div style={{ marginTop: 12 }}>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                        Minimum Surveys Required
                        <Tooltip title="If fewer surveys than this threshold are captured, the survey component contributes 0% and full weight goes to the QoC assessment.">
                            <InfoCircleOutlined style={{ marginLeft: 6, color: '#aaa' }} />
                        </Tooltip>
                    </label>
                    <Row gutter={12}>
                        <Col span={10}>
                            <div style={{fontSize:11,color:'#888',marginBottom:3}}>Fixed floor (absolute number)</div>
                            <InputNumber min={0} max={9999} value={form.min_surveys_required}
                                onChange={v => setForm(f => ({ ...f, min_surveys_required: v || 0 }))}
                                style={{ width: '100%' }} addonAfter="surveys"/>
                        </Col>
                        <Col span={10}>
                            <div style={{fontSize:11,color:'#888',marginBottom:3}}>% of facility patient baseline</div>
                            <InputNumber min={0} max={100} value={form.min_surveys_pct}
                                onChange={v => setForm(f => ({ ...f, min_surveys_pct: v || 0 }))}
                                style={{ width: '100%' }} formatter={v=>`${v}%`} parser={v=>v.replace('%','')}
                                addonAfter="%"/>
                        </Col>
                    </Row>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                        System enforces whichever is <strong>higher</strong>: the fixed floor, or the % of each facility's monthly patient volume baseline.<br/>
                        Facility baselines are set per facility in the Facilities module. Set both to 0 to always include survey scores.
                    </div>
                </div>
            </Card>

            {/* Live Preview */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}
                title={<span style={{ color: '#1890ff' }}>📊 Live Preview</span>}>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 12 }}>
                    Example: Facility scores <strong>85%</strong> on QoC, <strong>78%</strong> inpatient, <strong>72%</strong> outpatient:
                </div>
                <Row gutter={12} align="middle">
                    <Col span={6}>
                        <div style={{ textAlign: 'center', padding: 12, backgroundColor: '#e6f4ff', borderRadius: 8 }}>
                            <div style={{ fontSize: 11, color: '#888' }}>QoC Score</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: '#1890ff' }}>85%</div>
                            <div style={{ fontSize: 11, color: '#888' }}>× {form.qoc_weight}%</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1890ff' }}>= {(85 * form.qoc_weight / 100).toFixed(1)}</div>
                        </div>
                    </Col>
                    <Col span={2} style={{ textAlign: 'center', fontSize: 20, color: '#aaa' }}>+</Col>
                    <Col span={6}>
                        <div style={{ textAlign: 'center', padding: 12, backgroundColor: '#e6f9ee', borderRadius: 8 }}>
                            <div style={{ fontSize: 11, color: '#888' }}>Survey Score</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: '#52c41a' }}>{previewSurvey.toFixed(1)}%</div>
                            <div style={{ fontSize: 11, color: '#888' }}>× {form.survey_weight}%</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#52c41a' }}>= {(previewSurvey * form.survey_weight / 100).toFixed(1)}</div>
                        </div>
                    </Col>
                    <Col span={2} style={{ textAlign: 'center', fontSize: 20, color: '#aaa' }}>=</Col>
                    <Col span={8}>
                        <div style={{ textAlign: 'center', padding: 16,
                            background: 'linear-gradient(135deg,#1890ff,#52c41a)',
                            borderRadius: 10, color: '#fff' }}>
                            <div style={{ fontSize: 12, opacity: 0.85 }}>Quality Index</div>
                            <div style={{ fontSize: 32, fontWeight: 800 }}>{previewIndex.toFixed(1)}%</div>
                        </div>
                    </Col>
                </Row>
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    Rationale / Notes <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span>
                </label>
                <Input.TextArea rows={2} value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Increased survey weight for FY2024/25 based on National QMP directive..." />
            </Card>

            <div style={{ textAlign: 'right' }}>
                <Button type="primary" loading={loading} onClick={handleSave}
                    disabled={!totalCheck || !form.period_label}
                    icon={<CheckCircleOutlined />} size="large">
                    Save Period Weights
                </Button>
            </div>
        </div>
    );
}

// ─── KPI Targets Panel ────────────────────────────────────────────────────────
function KpiTargets({ clauses }) {
    const [kpis, setKpis] = useState([]);
    const [kpiVsActual, setKpiVsActual] = useState({ kpis: [], actuals: [] });
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState({});
    const [filters, setFilters] = useState({});

    const { periods: periodsList, currentPeriod: currentKPIPeriod } = usePeriods();

    useEffect(() => {
        // Auto-select current period in filter
        if (currentKPIPeriod) {
            setFilters(f => ({ ...f, period_label: currentKPIPeriod.label }));
        }
    }, [currentKPIPeriod]);

    useEffect(() => { loadKpis(); }, []);

    const loadKpis = async (f = filters) => {
        setLoading(true);
        try {
            const [kpiRes, vsRes] = await Promise.all([
                QoCService.getKpis({ ...f, kpi_type: 'target' }),
                QoCService.getKpiVsActual(f)
            ]);
            // data = kpi_targets array, period_config and kpi_vs_actual at top level
            setKpis(Array.isArray(kpiRes.data?.data) ? kpiRes.data.data : []);
            setKpiVsActual(vsRes.data?.data || { kpis: [], actuals: [] });
        } catch { message.error('Failed to load KPIs'); }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        if (!form.period_label || !form.target_pct || !form.period_start) {
            message.error('Period, target % and dates are required');
            return;
        }
        setLoading(true);
        try {
            await QoCService.saveKpi({
                ...form,
                kpi_type:     'target',
                period_start: moment(form.period_start).format('YYYY-MM-DD'),
                period_end:   moment(form.period_end).format('YYYY-MM-DD'),
            });

            const clauseCode = clauses.find(c => c.id === form.clause_id)?.code || 'Overall';
            trailCreate(TRAIL_MODULES.KPI, 'KPI Target', '', {
                period: form.period_label,
                clause: clauseCode,
                target: `${form.target_pct}%`,
                level: form.facility_level || 'All Levels',
            });
            message.success('KPI target saved');
            setModalOpen(false);
            setForm({});
            loadKpis();
        } catch { message.error('Failed to save'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {

        try {
            const kpi = kpis.find(k => k.id === id);
            await QoCService.deleteKpi(id);
            trailDelete(TRAIL_MODULES.KPI, `KPI Target ${kpi?.clause_code || 'Overall'} ${kpi?.period_label || ''}`, id,
                { period: kpi?.period_label, clause: kpi?.clause_code, target: kpi?.target_pct });
            message.success('Deleted');
            loadKpis();
        }
        catch { message.error('Failed to delete'); }
    };

    const comparisonData = (kpiVsActual.kpis || []).map(kpi => {
        const actuals = (kpiVsActual.actuals || []).filter(a =>
            (!kpi.clause_id || Number(a.clause_id) === Number(kpi.clause_id)) &&
            (!kpi.facility_level || a.site_type === kpi.facility_level)
        );
        const avg = actuals.length
            ? actuals.reduce((s, a) => s + parseFloat(a.total_score || 0), 0) / actuals.length
            : null;
        return { ...kpi, actual_score: avg, facilities_count: actuals.length };
    });

    const kpiColumns = [
        { title: 'Period', dataIndex: 'period_label', width: 110, render: p => <Tag color="blue">{p}</Tag> },
        { title: 'Dates', width: 200, render: (_, r) => `${moment(r.period_start).format('DD MMM YYYY')} – ${moment(r.period_end).format('DD MMM YYYY')}` },
        { title: 'Clause', dataIndex: 'clause_code', width: 150, render: (c, r) => c ? <Tag>{c} — {r.clause_title}</Tag> : <Tag color="purple">Overall</Tag> },
        { title: 'Level', dataIndex: 'facility_level', width: 120, render: l => l || <span style={{ color: '#ccc' }}>All Levels</span> },
        { title: 'Target', dataIndex: 'target_pct', width: 90, render: t => <span style={{ fontWeight: 700, color: '#1890ff', fontSize: 15 }}>{t}%</span> },
        { title: 'Set By', dataIndex: 'created_by_name', width: 130 },
        {
            title: '', width: 70,
            render: (_, r) => (
                <Popconfirm title="Delete this KPI target?" onConfirm={() => handleDelete(r.id)}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    const compColumns = [
        { title: 'Period', dataIndex: 'period_label', width: 100, render: p => <Tag color="blue">{p}</Tag> },
        { title: 'Clause', dataIndex: 'clause_code', width: 130, render: c => c || 'Overall' },
        { title: 'Level', dataIndex: 'facility_level', width: 110, render: l => l || 'All' },
        { title: 'Target', dataIndex: 'target_pct', width: 80, render: t => <strong>{t}%</strong> },
        {
            title: 'Actual (avg)', dataIndex: 'actual_score', width: 120,
            render: s => s != null
                ? <span style={{ fontWeight: 700, color: s >= 90 ? '#52c41a' : s >= 70 ? '#faad14' : '#f5222d' }}>{parseFloat(s).toFixed(1)}%</span>
                : <span style={{ color: '#ccc' }}>No data</span>
        },
        {
            title: 'vs Target', width: 180,
            render: (_, r) => {
                if (r.actual_score == null) return <Tag>No assessments yet</Tag>;
                const diff = parseFloat(r.actual_score) - parseFloat(r.target_pct);
                const met = diff >= 0;
                return (
                    <div>
                        <Tag color={met ? 'success' : 'error'}>{met ? `✓ +${diff.toFixed(1)}%` : `✗ ${diff.toFixed(1)}%`}</Tag>
                        <Progress percent={Math.min(100, (parseFloat(r.actual_score) / parseFloat(r.target_pct)) * 100)}
                            size="small" strokeColor={met ? '#52c41a' : '#f5222d'} showInfo={false} style={{ marginTop: 3 }} />
                    </div>
                );
            }
        },
        { title: 'Facilities', dataIndex: 'facilities_count', width: 80 }
    ];

    return (
        <div>
            <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16} align="bottom">
                    <Col span={6}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Period</label>
                        <Select allowClear placeholder="All Periods" style={{ width: '100%' }}
                            value={filters.period_label || undefined}
                            onChange={v => setFilters({ ...filters, period_label: v || '' })}>
                            {periodsList.map(p => (
                                <Select.Option key={p.label} value={p.label}>
                                    {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={5}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Clause</label>
                        <Select allowClear placeholder="All" value={filters.clause_id}
                            onChange={v => setFilters({ ...filters, clause_id: v })} style={{ width: '100%' }}>
                            {clauses.map(c => <Select.Option key={c.id} value={c.id}>{c.code}</Select.Option>)}
                        </Select>
                    </Col>
                    <Col span={3}><Button type="primary" onClick={() => loadKpis()}>Filter</Button></Col>
                    <Col span={10} style={{ textAlign: 'right' }}>
                        <Button type="primary" icon={<PlusOutlined />}
                            onClick={() => { setForm(currentKPIPeriod ? periodToForm(currentKPIPeriod) : {}); setModalOpen(true); }}>
                            Add KPI Target
                        </Button>
                    </Col>
                </Row>
            </Card>

            {comparisonData.length > 0 && (
                <Card title={<span><BarChartOutlined /> KPI vs Actual Performance</span>} style={{ marginBottom: 16 }}>
                    <Table columns={compColumns} dataSource={comparisonData} rowKey={(_, i) => i}
                        pagination={false} size="small" />
                </Card>
            )}

            <Card title={<span><TrophyOutlined /> Defined KPI Targets</span>}>
                <Table columns={kpiColumns} dataSource={kpis} rowKey="id" loading={loading}
                    pagination={{ pageSize: 20 }} />
            </Card>

            <Modal title="Add KPI Target" open={modalOpen}
                onCancel={() => setModalOpen(false)} onOk={handleSave}
                confirmLoading={loading} okText="Save" width={560}>
                <Row gutter={16}>
                    <Col span={12}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Period Label *</label>
                        <Select style={{ width: '100%' }}
                            value={form.period_label || undefined}
                            onChange={v => {
                                const p = periodsList.find(x => x.label === v);
                                if (p) setForm(f => ({ ...f, ...periodToForm(p) }));
                            }}>
                            {periodsList.map(p => (
                                <Select.Option key={p.label} value={p.label}>
                                    {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Target % *</label>
                        <InputNumber min={0} max={100} value={form.target_pct} step={0.5}
                            onChange={v => setForm({ ...form, target_pct: v })} style={{ width: '100%' }}
                            formatter={v => `${v}%`} parser={v => v.replace('%', '')} />
                    </Col>
                    <Col span={24} style={{ marginTop: 12 }}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Period Dates *</label>
                        <RangePicker value={[form.period_start, form.period_end]}
                            disabled
                            onChange={v => setForm({ ...form, period_start: v?.[0], period_end: v?.[1] })}
                            style={{ width: '100%' }} />
                    </Col>
                    <Col span={12} style={{ marginTop: 12 }}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Clause (blank = overall)</label>
                        <Select allowClear placeholder="Overall" value={form.clause_id}
                            onChange={v => setForm({ ...form, clause_id: v })} style={{ width: '100%' }}>
                            {clauses.map(c => <Select.Option key={c.id} value={c.id}>{c.code} — {c.title}</Select.Option>)}
                        </Select>
                    </Col>
                    <Col span={12} style={{ marginTop: 12 }}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Facility Level (blank = all)</label>
                        <Select allowClear placeholder="All Levels" value={form.facility_level}
                            onChange={v => setForm({ ...form, facility_level: v })} style={{ width: '100%' }}>
                            {FACILITY_LEVELS.map(l => <Select.Option key={l} value={l}>{l}</Select.Option>)}
                        </Select>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function QoCKPIs() {
    const [clauses, setClauses] = useState([]);
    const [activeTab, setActiveTab] = useState('weights');

    useEffect(() => {
        trailPageView(TRAIL_MODULES.KPI, window.location.pathname);
        QoCService.getClauses()
            .then(res => setClauses(res.data?.data || []))
            .catch(() => {});
    }, []);

    const TAB_STYLE = (key) => ({
        padding: '10px 20px',
        cursor: 'pointer',
        borderBottom: activeTab === key ? '3px solid #1890ff' : '3px solid transparent',
        fontWeight: activeTab === key ? 700 : 400,
        color: activeTab === key ? '#1890ff' : '#555',
        fontSize: 14,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all 0.2s',
        userSelect: 'none',
    });

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>KPI Targets & Period Weights</h2></div>
            </div>
            <div className="page-pad">
                {/* Custom tab bar */}
                <div style={{
                    display: 'flex', borderBottom: '1px solid #e8e8e8',
                    marginBottom: 24, backgroundColor: '#fff',
                    borderRadius: '8px 8px 0 0', paddingLeft: 8,
                }}>
                    <div style={TAB_STYLE('weights')} onClick={() => setActiveTab('weights')}>
                        <SettingOutlined /> Index Weights
                    </div>
                    <div style={TAB_STYLE('targets')} onClick={() => setActiveTab('targets')}>
                        <TrophyOutlined /> KPI Targets
                    </div>
                </div>

                {/* Tab content */}
                {activeTab === 'weights' && (
                    <PeriodWeightConfig clauses={clauses} onSaved={() => {}} />
                )}
                {activeTab === 'targets' && (
                    <KpiTargets clauses={clauses} />
                )}
            </div>
        </div>
    );
}





